import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

export type MfaStatus = {
  enabled: boolean;
  enabledAt: string | null;
  recoveryCodesRemaining: number;
};

export type MfaSetup = {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
};

const MFA_KEY = ["users", "me", "mfa"] as const;

export function useMfaStatus(): UseQueryResult<MfaStatus> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...MFA_KEY, accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: () => apiFetch<MfaStatus>("/users/me/mfa", { token: accessToken }),
  });
}

export function useBeginMfaSetup() {
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: () =>
      apiFetch<MfaSetup>("/users/me/mfa/setup", {
        method: "POST",
        token: accessToken,
      }),
  });
}

export function useEnableMfa() {
  const qc = useQueryClient();
  const { accessToken, refreshUser } = useAuth();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ recoveryCodes: string[] }>("/users/me/mfa/enable", {
        method: "POST",
        body: { code },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MFA_KEY });
      void refreshUser();
    },
  });
}

export function useDisableMfa() {
  const qc = useQueryClient();
  const { accessToken, refreshUser } = useAuth();
  return useMutation({
    mutationFn: (payload: { password: string; code: string }) =>
      apiFetch<{ success: true }>("/users/me/mfa/disable", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MFA_KEY });
      void refreshUser();
    },
  });
}

export function useRegenerateRecoveryCodes() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: (code: string) =>
      apiFetch<{ recoveryCodes: string[] }>("/users/me/mfa/recovery-codes", {
        method: "POST",
        body: { code },
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: MFA_KEY });
    },
  });
}
